import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { waLink, WA_MESSAGES } from "@/lib/whatsapp";

const links = [
  { to: "/", label: "Home" },
  { to: "/programs", label: "Programs" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-sand-100/90 border-b border-sand-300 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-sand-100/60 backdrop-blur-sm"
      }`}
    >
      <div className="container-x flex h-16 md:h-[72px] items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
          aria-label="Weight Loss Gurukul home"
        >
          <img
            src="/logo.png"
            alt="Weight Loss Gurukul"
            className="h-11 w-auto md:h-14 object-contain shrink-0"
          />
          <span className="font-serif text-lg md:text-xl font-semibold text-forest tracking-tight leading-none">
            Weight Loss <span className="italic text-clay">Gurukul</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive ? "text-forest font-medium" : "text-ink-soft hover:text-forest"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="nav-consultation-cta"
            href={waLink(WA_MESSAGES.trial)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-accent text-sm px-5 py-2.5"
          >
            Book Your Free Consultation
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 rounded-full hover:bg-sand-200"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-sand-300 bg-sand-100/95 backdrop-blur-xl">
          <div className="container-x py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-2 text-base ${isActive ? "text-forest font-medium" : "text-ink-soft"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              data-testid="mobile-nav-consultation-cta"
              href={waLink(WA_MESSAGES.trial)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="btn-accent mt-2 w-full"
            >
              Book Your Free Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
